<template>
  <div class="range-picker" :class="{ 'range-picker--open': isOpen }">
    <button
      type="button"
      class="range-picker__trigger"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      @click="togglePopover"
    >
      <svg class="range-picker__icon" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M3 6h10M3 9h10M3 12h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/>
        <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" stroke="currentColor" stroke-width="1.5" fill="none"/>
      </svg>
      <span class="range-picker__label">{{ triggerLabel }}</span>
      <svg class="range-picker__chevron" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
      </svg>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="popoverRef"
        class="range-picker__popover"
        role="dialog"
        aria-label="Choose date range"
        :style="popoverStyle"
        @click.stop
      >
        <section class="range-picker__panel range-picker__panel--quick" aria-label="Quick ranges">
          <div class="range-picker__quick-grid">
            <button
              v-for="preset in presets"
              :key="preset.label"
              type="button"
              class="range-picker__chip"
              :class="{ 'range-picker__chip--active': isPresetActive(preset) }"
              @click="selectPreset(preset)"
            >
              {{ preset.label }}
            </button>
          </div>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch, type PropType } from 'vue'

export type Granularity = 'minute' | 'hour' | 'day' | 'week'

export type RangeSpec =
  | { kind: 'preset'; days: number; granularity: Granularity; label: string }
  | { kind: 'absolute'; from: string; to: string; granularity: Granularity }
  | { kind: 'all'; granularity: Granularity; label: string }

export type DataSpan = {
  firstEventTs: string | null
  lastEventTs: string | null
  totalObjects: number
} | null

type Preset = { label: string; days: number; granularity: Granularity }

// Preset granularity matches the natural bucket count for the window so
// the chart x-axis always renders a clean grid (24 hourly slots for 24h,
// N daily slots for N days). Consumers calendar-align from/to at query
// time so the canvas spans the full requested window edge-to-edge.
const PRESETS: Preset[] = [
  { label: 'Last 1 hour', days: 1 / 24, granularity: 'minute' },
  { label: 'Last 24 hours', days: 1, granularity: 'hour' },
  { label: 'Last 3 days', days: 3, granularity: 'day' },
  { label: 'Last 7 days', days: 7, granularity: 'day' },
  { label: 'Last 30 days', days: 30, granularity: 'day' },
  { label: 'Last 90 days', days: 90, granularity: 'day' },
  { label: 'Last 180 days', days: 180, granularity: 'week' },
  { label: 'Last 365 days', days: 365, granularity: 'week' },
]

const DAY_MS = 24 * 60 * 60 * 1000

// Mirror of backend granularity ladder — keeps the hint text honest about
// what bucketing the server will actually pick when granularity is left
// unspecified.
const granularityForWindow = (windowMs: number): Granularity => {
  if (windowMs <= 2 * DAY_MS) return 'minute'
  if (windowMs <= 14 * DAY_MS) return 'hour'
  if (windowMs <= 180 * DAY_MS) return 'day'
  return 'week'
}

const SLIDER_RES = 10000 // resolution for dual-thumb slider

const toLocalISO = (date: Date) => {
  const offset = date.getTimezoneOffset() * 60 * 1000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

const fromLocalISO = (value: string): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const formatRange = (from: Date, to: Date) => {
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }
  const fromLabel = from.toLocaleString(undefined, opts)
  const toLabel = to.toLocaleString(undefined, opts)
  return `${fromLabel} → ${toLabel}`
}

export default defineComponent({
  name: 'AnalyticsRangePicker',
  props: {
    modelValue: {
      type: Object as PropType<RangeSpec>,
      required: true,
    },
    dataSpan: {
      type: Object as PropType<DataSpan>,
      default: null,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const isOpen = ref(false)
    const popoverRef = ref<HTMLElement | null>(null)
    const triggerRect = ref<DOMRect | null>(null)
    const absoluteFrom = ref('')
    const absoluteTo = ref('')
    const sliderLeft = ref(0)
    const sliderRight = ref(SLIDER_RES)
    const draggingSlider = ref(false)
    const mode = ref<'quick' | 'absolute' | 'slider'>('quick')

    const tabs: Array<{ id: 'quick' | 'absolute' | 'slider'; label: string; icon: string }> = [
      { id: 'quick', label: 'Quick', icon: '⚡' },
      { id: 'absolute', label: 'Absolute', icon: '📅' },
      { id: 'slider', label: 'Slider', icon: '◐' },
    ]

    const presets = PRESETS

    const nowLocalISO = computed(() => toLocalISO(new Date()))

    const spanStart = computed(() => {
      const ts = props.dataSpan?.firstEventTs
      if (ts) {
        const t = Date.parse(ts)
        if (Number.isFinite(t)) return t
      }
      return Date.now() - 90 * DAY_MS
    })

    const spanEnd = computed(() => {
      const ts = props.dataSpan?.lastEventTs
      const lastEvent = ts ? Date.parse(ts) : NaN
      return Math.max(Number.isFinite(lastEvent) ? lastEvent : 0, Date.now())
    })

    const spanMs = computed(() => Math.max(1, spanEnd.value - spanStart.value))

    const sliderToTime = (pos: number) => spanStart.value + (pos / SLIDER_RES) * spanMs.value
    const timeToSlider = (ts: number) => Math.round(((ts - spanStart.value) / spanMs.value) * SLIDER_RES)

    const sliderPercentLeft = computed(() => (sliderLeft.value / SLIDER_RES) * 100)
    const sliderPercentRight = computed(() => (sliderRight.value / SLIDER_RES) * 100)
    const sliderWindowPercent = computed(() => Math.max(0, sliderPercentRight.value - sliderPercentLeft.value))

    const spanLabelLeft = computed(() => {
      const d = new Date(spanStart.value)
      return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' })
    })
    const spanLabelRight = computed(() => {
      const d = new Date(spanEnd.value)
      return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' })
    })

    const hasDataSpan = computed(() => Boolean(props.dataSpan?.firstEventTs))

    const triggerLabel = computed(() => {
      const value = props.modelValue
      if (value.kind === 'preset') return value.label
      if (value.kind === 'all') return value.label
      const from = new Date(value.from)
      const to = new Date(value.to)
      return formatRange(from, to)
    })

    const isPresetActive = (preset: Preset) => {
      const value = props.modelValue
      return value.kind === 'preset' && value.days === preset.days && value.granularity === preset.granularity
    }

    const isAllActive = computed(() => props.modelValue.kind === 'all')

    const sliderSummaryRange = computed(() => {
      const from = fromLocalISO(absoluteFrom.value)
      const to = fromLocalISO(absoluteTo.value)
      if (!from || !to) return '—'
      return formatRange(from, to)
    })

    const sliderSummaryMeta = computed(() => {
      const from = fromLocalISO(absoluteFrom.value)
      const to = fromLocalISO(absoluteTo.value)
      if (!from || !to || to <= from) return ''
      const ms = to.getTime() - from.getTime()
      const days = ms / DAY_MS
      const dayLabel = days < 1 ? `${(ms / (60 * 60 * 1000)).toFixed(1)} hours` : `${days.toFixed(1)} days`
      return `${dayLabel} · ${granularityForWindow(ms)} granularity`
    })

    const totalSpanLabel = computed(() => {
      const days = spanMs.value / DAY_MS
      if (days < 1) return `${(spanMs.value / (60 * 60 * 1000)).toFixed(1)} h`
      if (days < 60) return `${days.toFixed(0)} day window`
      return `${(days / 30).toFixed(1)} month window`
    })

    const popoverStyle = computed(() => {
      const rect = triggerRect.value
      if (!rect) return { display: 'none' }
      // Estimate popover height — flips above the trigger when there's not
      // enough room below the fold (slider gets clipped otherwise).
      const POPOVER_HEIGHT_EST = 360
      const spaceBelow = window.innerHeight - rect.bottom
      const flipUp = spaceBelow < POPOVER_HEIGHT_EST && rect.top > spaceBelow
      const top = flipUp
        ? rect.top + window.scrollY - POPOVER_HEIGHT_EST - 8
        : rect.bottom + window.scrollY + 8
      const left = Math.min(
        rect.left + window.scrollX,
        window.innerWidth - 540 - 16,
      )
      return {
        top: `${top}px`,
        left: `${Math.max(16, left)}px`,
      }
    })

    const syncSliderFromAbsolute = () => {
      const from = fromLocalISO(absoluteFrom.value)
      const to = fromLocalISO(absoluteTo.value)
      if (!from || !to) return
      const left = Math.max(0, Math.min(SLIDER_RES, timeToSlider(from.getTime())))
      const right = Math.max(0, Math.min(SLIDER_RES, timeToSlider(to.getTime())))
      sliderLeft.value = Math.min(left, right)
      sliderRight.value = Math.max(left, right)
    }

    const togglePopover = (event: MouseEvent) => {
      if (isOpen.value) {
        closePopover()
        return
      }
      const trigger = (event.currentTarget as HTMLElement) || null
      triggerRect.value = trigger?.getBoundingClientRect() ?? null
      const value = props.modelValue
      if (value.kind === 'absolute') {
        absoluteFrom.value = toLocalISO(new Date(value.from))
        absoluteTo.value = toLocalISO(new Date(value.to))
        mode.value = 'absolute'
      } else if (value.kind === 'all') {
        absoluteFrom.value = toLocalISO(new Date(spanStart.value))
        absoluteTo.value = toLocalISO(new Date(spanEnd.value))
        mode.value = 'slider'
      } else {
        const to = new Date()
        const from = new Date(to.getTime() - value.days * DAY_MS)
        absoluteFrom.value = toLocalISO(from)
        absoluteTo.value = toLocalISO(to)
        mode.value = 'quick'
      }
      syncSliderFromAbsolute()
      isOpen.value = true
    }

    const closePopover = () => {
      isOpen.value = false
    }

    const selectPreset = (preset: Preset) => {
      emit('update:modelValue', {
        kind: 'preset',
        days: preset.days,
        granularity: preset.granularity,
        label: preset.label,
      } satisfies RangeSpec)
      closePopover()
    }

    const selectAll = () => {
      emit('update:modelValue', {
        kind: 'all',
        granularity: granularityForWindow(spanMs.value),
        label: 'All data',
      } satisfies RangeSpec)
      closePopover()
    }

    const canApply = computed(() => {
      const from = fromLocalISO(absoluteFrom.value)
      const to = fromLocalISO(absoluteTo.value)
      return Boolean(from && to && to > from)
    })

    const absoluteHint = computed(() => {
      const from = fromLocalISO(absoluteFrom.value)
      const to = fromLocalISO(absoluteTo.value)
      if (!from || !to) return 'Pick a from and to date+time.'
      if (to <= from) return 'End must be after start.'
      const windowMs = to.getTime() - from.getTime()
      const spanDays = windowMs / DAY_MS
      const g = granularityForWindow(windowMs)
      const dayLabel = spanDays < 1 ? `${spanDays.toFixed(2)} days` : `${spanDays.toFixed(1)} days`
      const pctLabel = hasDataSpan.value
        ? ` Showing ${sliderWindowPercent.value.toFixed(0)}% of available data.`
        : ''
      return `${dayLabel} · ${g} granularity.${pctLabel}`
    })

    const applyAbsolute = () => {
      const from = fromLocalISO(absoluteFrom.value)
      const to = fromLocalISO(absoluteTo.value)
      if (!from || !to || to <= from) return
      const granularity = granularityForWindow(to.getTime() - from.getTime())
      emit('update:modelValue', {
        kind: 'absolute',
        from: from.toISOString(),
        to: to.toISOString(),
        granularity,
      } satisfies RangeSpec)
      closePopover()
    }

    const onSliderLeftInput = (event: Event) => {
      draggingSlider.value = true
      const v = Number((event.target as HTMLInputElement).value)
      sliderLeft.value = Math.min(v, sliderRight.value - 1)
      absoluteFrom.value = toLocalISO(new Date(sliderToTime(sliderLeft.value)))
      requestAnimationFrame(() => { draggingSlider.value = false })
    }

    const onSliderRightInput = (event: Event) => {
      draggingSlider.value = true
      const v = Number((event.target as HTMLInputElement).value)
      sliderRight.value = Math.max(v, sliderLeft.value + 1)
      absoluteTo.value = toLocalISO(new Date(sliderToTime(sliderRight.value)))
      requestAnimationFrame(() => { draggingSlider.value = false })
    }

    watch([absoluteFrom, absoluteTo], () => {
      if (draggingSlider.value) return
      syncSliderFromAbsolute()
    })

    watch(() => props.dataSpan, () => {
      if (isOpen.value) syncSliderFromAbsolute()
    })

    const handleDocumentClick = (event: MouseEvent) => {
      if (!isOpen.value) return
      const target = event.target as Node
      if (popoverRef.value?.contains(target)) return
      const trigger = document.querySelector('.range-picker__trigger')
      if (trigger?.contains(target)) return
      closePopover()
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen.value) closePopover()
    }

    onMounted(() => {
      document.addEventListener('mousedown', handleDocumentClick)
      document.addEventListener('keydown', handleKeydown)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('mousedown', handleDocumentClick)
      document.removeEventListener('keydown', handleKeydown)
    })

    watch(isOpen, async (open) => {
      if (!open) return
      await nextTick()
      const firstPreset = popoverRef.value?.querySelector<HTMLElement>('.range-picker__preset--active') ||
        popoverRef.value?.querySelector<HTMLElement>('.range-picker__preset')
      firstPreset?.focus({ preventScroll: true })
    })

    return {
      isOpen,
      popoverRef,
      popoverStyle,
      presets,
      isPresetActive,
      isAllActive,
      triggerLabel,
      nowLocalISO,
      absoluteFrom,
      absoluteTo,
      absoluteHint,
      canApply,
      togglePopover,
      closePopover,
      selectPreset,
      selectAll,
      applyAbsolute,
      sliderLeft,
      sliderRight,
      sliderPercentLeft,
      sliderPercentRight,
      sliderWindowPercent,
      spanLabelLeft,
      spanLabelRight,
      hasDataSpan,
      onSliderLeftInput,
      onSliderRightInput,
      SLIDER_RES,
      mode,
      tabs,
      sliderSummaryRange,
      sliderSummaryMeta,
      totalSpanLabel,
    }
  },
})
</script>

<style scoped>
.range-picker {
  display: inline-block;
}

.range-picker__trigger {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(45, 49, 73, 0.7);
  color: #ffffff;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  min-height: 38px;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.range-picker__trigger:hover {
  background: rgba(75, 81, 110, 0.85);
  border-color: rgba(255, 255, 255, 0.35);
}

.range-picker--open .range-picker__trigger {
  border-color: var(--vt-c-accent, #65f0c2);
}

.range-picker__icon,
.range-picker__chevron {
  width: 14px;
  height: 14px;
  display: block;
  flex: none;
}

.range-picker__label {
  white-space: nowrap;
}
</style>

<style>
.range-picker__popover {
  position: absolute;
  z-index: 9999;
  width: 420px;
  max-width: calc(100vw - 32px);
  background: #1b1f33;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
  font-family: var(--ui-inverse-font-family, system-ui);
  color: #ffffff;
  padding: 12px;
}

.range-picker__preset--all {
  margin-top: 6px;
  border-top: 1px dashed rgba(255, 255, 255, 0.12);
  padding-top: 10px;
  font-style: italic;
  color: rgba(255, 255, 255, 0.85);
}

.range-picker__slider {
  padding: 12px 16px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.range-picker__slider-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}

.range-picker__slider-head h4 {
  margin: 0;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
}

.range-picker__slider-pct {
  font-size: 0.78rem;
  color: var(--vt-c-accent, #65f0c2);
  font-weight: 600;
}

.range-picker__slider-pct--muted {
  color: rgba(255, 255, 255, 0.35);
  font-style: italic;
}

.range-picker__slider-track-wrap {
  position: relative;
  height: 28px;
  margin: 6px 0;
}

.range-picker__slider-track-wrap.is-disabled {
  opacity: 0.45;
  pointer-events: none;
}

.range-picker__slider-track {
  position: absolute;
  top: 12px;
  left: 0;
  right: 0;
  height: 4px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 2px;
}

.range-picker__slider-fill {
  position: absolute;
  top: 12px;
  height: 4px;
  background: var(--vt-c-accent, #65f0c2);
  border-radius: 2px;
  pointer-events: none;
}

.range-picker__slider-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 28px;
  margin: 0;
  appearance: none;
  background: transparent;
  pointer-events: none;
}

.range-picker__slider-input::-webkit-slider-thumb {
  appearance: none;
  pointer-events: auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid var(--vt-c-accent, #65f0c2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  cursor: grab;
  margin-top: -7px;
}

.range-picker__slider-input::-webkit-slider-thumb:active {
  cursor: grabbing;
  transform: scale(1.08);
}

.range-picker__slider-input::-moz-range-thumb {
  pointer-events: auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid var(--vt-c-accent, #65f0c2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  cursor: grab;
}

.range-picker__slider-input::-webkit-slider-runnable-track {
  background: transparent;
  height: 28px;
}

.range-picker__slider-input::-moz-range-track {
  background: transparent;
  height: 28px;
}

.range-picker__slider-edges {
  display: flex;
  justify-content: space-between;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 4px;
}

/* Tabbed redesign */

.range-picker__tabs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 6px 6px 0;
  background: rgba(0, 0, 0, 0.18);
  border-radius: 14px 14px 0 0;
}

.range-picker__tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 8px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.range-picker__tab:hover {
  color: rgba(255, 255, 255, 0.85);
}

.range-picker__tab--active {
  color: var(--vt-c-accent, #65f0c2);
  border-bottom-color: var(--vt-c-accent, #65f0c2);
}

.range-picker__tab-icon {
  font-size: 1rem;
  line-height: 1;
}

.range-picker__panel {
  padding: 16px 18px;
  min-height: 220px;
}

.range-picker__panel--quick {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.range-picker__quick-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.range-picker__chip {
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.025);
  color: #e7eaf3;
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.range-picker__chip:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.18);
}

.range-picker__chip--active {
  background: var(--vt-c-accent, #65f0c2);
  border-color: var(--vt-c-accent, #65f0c2);
  color: #0e1124;
  font-weight: 600;
}

.range-picker__chip--active:hover:not(:disabled) {
  background: var(--vt-c-accent, #65f0c2);
  border-color: var(--vt-c-accent, #65f0c2);
}

.range-picker__chip:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.range-picker__chip--all {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 14px;
  border-style: dashed;
  border-color: rgba(101, 240, 194, 0.35);
  background: rgba(101, 240, 194, 0.05);
}

.range-picker__chip-icon {
  font-size: 1.4rem;
  font-weight: 400;
  line-height: 1;
  color: var(--vt-c-accent, #65f0c2);
}

.range-picker__chip-meta {
  font-size: 0.72rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 0.02em;
}

.range-picker__chip-meta--muted {
  color: rgba(255, 255, 255, 0.35);
  font-style: italic;
}

.range-picker__panel--absolute {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.range-picker__panel--absolute .range-picker__field input {
  font-size: 0.95rem;
  padding: 10px 12px;
}

.range-picker__panel--slider {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.range-picker__slider-summary {
  text-align: center;
  padding: 4px 0 0;
}

.range-picker__slider-summary-main {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #ffffff;
}

.range-picker__slider-summary-meta {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 2px;
}

.range-picker__slider-pct-row {
  text-align: center;
}

.range-picker__columns {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 0;
}

.range-picker__column {
  padding: 14px 16px;
}

.range-picker__column + .range-picker__column {
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

.range-picker__column h4 {
  margin: 0 0 10px;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
}

.range-picker__column ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.range-picker__preset {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: #e7eaf3;
  cursor: pointer;
  font-size: 0.88rem;
  transition: background 0.12s ease, border-color 0.12s ease;
}

.range-picker__preset:hover {
  background: rgba(255, 255, 255, 0.06);
}

.range-picker__preset--active {
  background: rgba(101, 240, 194, 0.16);
  border-color: rgba(101, 240, 194, 0.45);
  color: #ffffff;
  font-weight: 600;
}

.range-picker__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.65);
}

.range-picker__field input {
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 8px 10px;
  color: #ffffff;
  font-size: 0.9rem;
  font-family: inherit;
  color-scheme: dark;
}

.range-picker__field input:focus {
  outline: none;
  border-color: var(--vt-c-accent, #65f0c2);
  box-shadow: 0 0 0 2px rgba(101, 240, 194, 0.18);
}

.range-picker__hint {
  margin: 4px 0 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.35;
}

.range-picker__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.18);
  border-radius: 0 0 14px 14px;
}

.range-picker__btn {
  min-height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}

.range-picker__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.08);
}

.range-picker__btn--primary {
  background: var(--vt-c-accent, #65f0c2);
  color: #16182a;
  border-color: var(--vt-c-accent, #65f0c2);
}

.range-picker__btn--primary:hover:not(:disabled) {
  filter: brightness(1.05);
}

.range-picker__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
